import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Menu, X, ShoppingBag, BadgePercent } from "lucide-react";
import { useState, useEffect } from "react";
import api from "../lib/api";

export function Logo({ size = "md", className = "" }) {
  const h = size === "lg" ? "h-14" : size === "md" ? "h-10" : "h-9";
  return (
    <img
      src="/cake-society-logo.png"
      alt="Cake Society"
      className={`${h} w-auto object-contain ${className}`}
    />
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [isMember, setIsMember] = useState(false);

  useEffect(() => {
    if (user && user !== false && user.role !== "admin") {
      api.get("/membership/me").then(({ data }) => setIsMember(!!data?.active)).catch(() => setIsMember(false));
    } else {
      setIsMember(false);
    }
  }, [user]);

  const links = [
    { to: "/classes", label: "Classes & Camps" },
    { to: "/parties", label: "Parties" },
    { to: "/open-studio", label: "Open Studio" },
    { to: "/shop", label: "Shop" },
    { to: "/tutorials", label: "Vault" },
    { to: "/membership", label: "Membership" },
    { to: "/guest-creators", label: "Guest Creators" },
    { to: "/about", label: "About" },
  ];

  return (
    <header data-testid="navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F2E9D8]/85 border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo"><Logo size="sm" /></Link>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
          {links.map((l) => (
            <Link
              key={l.to} to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-[11px] tracking-[0.12em] uppercase font-medium text-[#121212] hover:text-[#121212] relative group whitespace-nowrap"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#C6FF33] origin-left scale-x-0 group-hover:scale-x-100 transition-transform" />
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 pl-3">
          <Link to="/cart" data-testid="nav-cart" aria-label="Cart" className="relative text-[#121212] hover:opacity-70">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#E0218A] text-white text-[10px] font-bold rounded-full">{count}</span>
            )}
          </Link>
          {user && user !== false ? (
            <>
              {isMember && (
                <span data-testid="member-badge" title="You're saving 15% as a member" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C6FF33] text-[#121212] text-[10px] tracking-[0.12em] uppercase font-bold whitespace-nowrap">
                  <BadgePercent size={13} /> Member · 15% off
                </span>
              )}
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="nav-dashboard"
                className="text-[11px] tracking-[0.15em] uppercase font-medium text-[#121212] whitespace-nowrap"
              >
                {user.role === "admin" ? "Admin" : "My Account"}
              </Link>
              <button
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="px-4 py-2.5 bg-[#121212] text-[#F2E9D8] text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-black transition-all whitespace-nowrap"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-[11px] tracking-[0.15em] uppercase font-medium text-[#121212] whitespace-nowrap">
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="px-4 py-2.5 bg-[#121212] text-[#F2E9D8] text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-black transition-all"
              >
                Join
              </Link>
            </>
          )}
        </div>

        <div className="xl:hidden flex items-center gap-4">
          <Link to="/cart" data-testid="nav-cart-mobile" aria-label="Cart" className="relative text-[#121212]">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#E0218A] text-white text-[10px] font-bold rounded-full">{count}</span>
            )}
          </Link>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-black/10 bg-[#F2E9D8]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase">
                {l.label}
              </Link>
            ))}
            {user && user !== false ? (
              <>
                {isMember && (
                  <span data-testid="member-badge-mobile" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C6FF33] text-[#121212] text-[11px] tracking-[0.15em] uppercase font-bold w-fit">
                    <BadgePercent size={13} /> Member · 15% off
                  </span>
                )}
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase">
                  {user.role === "admin" ? "Admin" : "My Account"}
                </Link>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left text-sm tracking-[0.2em] uppercase">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase">Join</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
